<template>
  <v-container fluid class="pa-6">
    <v-card class="border mb-6 pb-4" elevation="0">
      <v-card-title class="text-h4 font-weight-bold d-flex flex-wrap align-center justify-space-between ga-3">
        <div>
          <v-icon icon="mdi-clipboard-text-outline" class="mr-2" />
          Account Configuration
        </div>
        <div class="d-flex align-center ga-2">
          <v-btn
            variant="text"
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            prepend-icon="mdi-refresh"
            @click="loadLatest"
          >
            Refresh
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!snapshot || isLoading"
            prepend-icon="mdi-download"
            @click="downloadSnapshot"
          >
            Download Report
          </v-btn>
        </div>
      </v-card-title>
      <v-card-subtitle>
        Published configuration currently applied to inventory processing.
      </v-card-subtitle>
    </v-card>

    <v-card v-if="isLoading" class="border pa-8 text-center" elevation="0">
      <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
      <div class="text-h6 mb-2">Loading account configuration…</div>
      <div class="text-body-2 text-grey">
        Fetching the latest published snapshot for this account.
      </div>
    </v-card>

    <v-card v-else-if="!accountStore.accountId" class="border pa-8 text-center" elevation="0">
      <v-icon icon="mdi-account-question-outline" size="64" color="grey" class="mb-4" />
      <div class="text-h6 mb-2">No account selected</div>
      <div class="text-body-2 text-grey">
        Select a dealer account to view its published configuration.
      </div>
    </v-card>

    <v-card
      v-else-if="!snapshot"
      class="border pa-8 text-center"
      elevation="0"
      data-testid="account-config-empty-state"
    >
      <v-icon
        :icon="emptyState.icon"
        size="64"
        :color="emptyState.iconColor"
        class="mb-4"
      />
      <div class="text-h6 mb-2">{{ emptyState.title }}</div>
      <div class="text-body-2 text-grey">{{ emptyState.message }}</div>
      <v-alert
        v-if="loadError"
        type="error"
        variant="tonal"
        density="comfortable"
        class="mt-4 text-start"
      >
        {{ loadError }}
      </v-alert>
    </v-card>

    <div v-else>
      <v-alert
        v-if="snapshot.issues && snapshot.issues.length"
        type="warning"
        variant="tonal"
        density="comfortable"
        class="mb-4"
      >
        <div class="text-subtitle-2 mb-1">
          {{ snapshot.issues.length }} non-fatal load
          {{ snapshot.issues.length === 1 ? 'issue' : 'issues' }} were captured in this snapshot
        </div>
        <ul class="mb-0 ps-4">
          <li v-for="(issue, i) in snapshot.issues" :key="i">
            <strong>{{ issue.productId }}</strong>: {{ issue.message }}
          </li>
        </ul>
      </v-alert>

      <v-card class="border mb-4" elevation="0">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-database" class="mr-2" />
          Primary Sources
          <v-spacer />
          <v-chip size="small" variant="tonal" color="primary">
            {{ normalized.primarySources.effective.length }} effective
          </v-chip>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="!normalized.primarySources.effective.length" class="text-body-2 text-grey">
            No primary sources are configured. Inventory will fall back to the configured
            listing behavior.
          </div>
          <v-table v-else density="comfortable">
            <thead>
              <tr>
                <th>Feed</th>
                <th>Import product</th>
                <th>Pattern</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="feed in normalized.primarySources.feeds" :key="feed.feed">
                <td class="font-weight-medium">{{ feed.feed }}</td>
                <td>
                  <v-chip
                    v-if="feed.importProductFound"
                    size="x-small"
                    variant="tonal"
                    color="success"
                    prepend-icon="mdi-check"
                  >
                    found
                  </v-chip>
                  <v-chip
                    v-else
                    size="x-small"
                    variant="tonal"
                    color="error"
                    prepend-icon="mdi-alert"
                  >
                    missing import_{{ feed.feed }}
                  </v-chip>
                </td>
                <td>
                  <span v-if="feed.hasPattern" class="text-mono">{{ feed.pattern }}</span>
                  <v-chip v-else size="x-small" variant="tonal" color="warning">
                    no pattern
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-table>

          <v-alert
            v-if="normalized.primarySources.unsupported.length"
            type="warning"
            variant="tonal"
            density="comfortable"
            class="mt-4"
          >
            <div class="text-subtitle-2 mb-1">
              {{ normalized.primarySources.unsupported.length }} unsupported
              {{ normalized.primarySources.unsupported.length === 1 ? 'entry' : 'entries' }}
              in <code>ise.primarySources</code>
            </div>
            <ul class="mb-0 ps-4">
              <li
                v-for="(item, i) in normalized.primarySources.unsupported"
                :key="i"
                class="text-mono"
              >
                {{ formatUnsupported(item) }}
              </li>
            </ul>
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card class="border mb-4" elevation="0">
        <v-card-title class="d-flex flex-wrap align-center ga-3">
          <div class="d-flex align-center">
            <v-icon icon="mdi-file-tree" class="mr-2" />
            Field Mappings
          </div>
          <v-spacer />
          <v-text-field
            v-model="mappingSearch"
            density="compact"
            variant="outlined"
            hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search field, source, or path"
            class="mapping-search"
          />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="!filteredMappingScopes.length" class="text-body-2 text-grey">
            No field mappings are available in this snapshot.
          </div>
          <v-expansion-panels v-else variant="accordion">
            <v-expansion-panel
              v-for="scope in filteredMappingScopes"
              :key="scope.key"
              :title="scope.label"
            >
              <template #text>
                <div class="d-flex flex-wrap align-center ga-2 mb-4">
                  <v-chip size="small" variant="tonal" color="primary">
                    {{ scope.mappings.length }} mappings
                  </v-chip>
                  <v-chip v-if="scope.lastUpdated" size="small" variant="tonal">
                    updated
                    <DateTimeWithDealerTooltip :value="scope.lastUpdated" class="ms-1" />
                  </v-chip>
                </div>
                <v-table density="comfortable">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Mapped From</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="mapping in scope.mappings" :key="`${scope.key}-${mapping.field}`">
                      <td class="font-weight-medium">{{ mapping.field }}</td>
                      <td>
                        <div
                          v-for="(path, source) in mapping.sourceFieldPaths"
                          :key="`${mapping.field}-${source}-path`"
                          class="text-body-2 mb-1"
                        >
                          <span class="text-grey">{{ source }}:</span>
                          <span class="text-mono ms-1">{{ path }}</span>
                        </div>
                        <div v-if="mapping.defaultValue" class="text-body-2">
                          <span class="text-grey">default:</span>
                          <span class="text-mono ms-1">{{ mapping.defaultValue }}</span>
                        </div>
                        <span v-if="!Object.keys(mapping.sourceFieldPaths).length">—</span>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <v-card class="border mb-4" elevation="0">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-format-list-checks" class="mr-2" />
          Publish Rules
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="!listingRuleScopes.length" class="text-body-2 text-grey">
            No publish rules are configured in this snapshot.
          </div>
          <v-expansion-panels v-else variant="accordion">
            <v-expansion-panel
              v-for="scope in listingRuleScopes"
              :key="scope.key"
              :title="scope.label"
            >
              <template #text>
                <div class="d-flex flex-wrap align-center ga-2 mb-4">
                  <v-chip size="small" variant="tonal" color="primary">
                    {{ scope.groups.length }} rule {{ scope.groups.length === 1 ? 'group' : 'groups' }}
                  </v-chip>
                  <v-chip v-if="scope.evaluationMode" size="small" variant="tonal">
                    {{ formatEvaluationMode(scope.evaluationMode) }}
                  </v-chip>
                </div>
                <div class="d-flex flex-column ga-3">
                  <v-sheet
                    v-for="(group, index) in scope.groups"
                    :key="`${scope.key}-${index}`"
                    border
                    rounded
                    class="pa-4"
                  >
                    <div class="d-flex flex-wrap align-center ga-2 mb-2">
                      <v-chip v-if="group.action" size="small" variant="tonal" color="primary">
                        {{ formatActionLabel(group.action) }}
                      </v-chip>
                      <v-chip v-if="group.logic" size="small" variant="tonal">
                        {{ formatLogicLabel(group.logic) }}
                      </v-chip>
                    </div>
                    <ul class="rule-lines mb-0">
                      <li v-for="(line, lineIndex) in group.conditions" :key="lineIndex">
                        {{ line }}
                      </li>
                    </ul>
                  </v-sheet>
                </div>
              </template>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-alert
            v-if="normalized.listingRules.legacyPrimarySourcesPresent"
            type="warning"
            variant="tonal"
            density="comfortable"
            class="mt-4"
          >
            <div class="text-subtitle-2">Config drift detected</div>
            <div class="text-body-2">
              <code>ise_inventory_de_listing.primarySources</code> is set but is no longer
              authoritative. The runtime uses <code>ise.primarySources</code> as the single
              source of truth. The legacy field is safe to remove.
            </div>
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card class="border mb-4" elevation="0">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-layers-triple-outline" class="mr-2" />
          Segments
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="!segments.length" class="text-body-2 text-grey">
            No segments are configured in this snapshot.
          </div>
          <v-expansion-panels v-else variant="accordion">
            <v-expansion-panel
              v-for="segment in segments"
              :key="segment.key"
              :title="segment.label"
            >
              <template #text>
                <div class="text-body-2 text-grey mb-4">
                  {{ describeSegment(segment) }}
                </div>
                <div v-if="segment.conditions.length">
                  <ul class="rule-lines mb-0">
                    <li v-for="(line, lineIndex) in segment.conditions" :key="lineIndex">
                      {{ line }}
                    </li>
                  </ul>
                </div>
                <div v-else class="text-body-2 text-grey">
                  Applies to all vehicles unless matched by a higher-priority segment.
                </div>
              </template>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <v-card v-if="preProcessorDisplay" class="border mb-4" elevation="0">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-merge" class="mr-2" />
          Pre-Processor
          <v-spacer />
          <v-chip
            size="small"
            variant="tonal"
            :color="preProcessorModeColor(preProcessorDisplay.mode)"
          >
            {{ formatPreProcessorMode(preProcessorDisplay.mode) }}
          </v-chip>
          <v-chip
            v-if="preProcessorDisplay.productFound && !preProcessorDisplay.isActive"
            size="small"
            variant="tonal"
            color="warning"
            class="ms-2"
          >
            inactive
          </v-chip>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div
            v-if="preProcessorDisplay.mode === 'none'"
            class="text-body-2 text-grey"
          >
            No pre-processor is configured for this account. Vehicles flow directly from
            the dealer's own primary feeds.
          </div>

          <div
            v-else-if="preProcessorDisplay.mode === 'unknown'"
            class="text-body-2 text-grey"
          >
            A pre-processor is configured but its mode could not be safely identified.
            See the raw snapshot below for the exact payload.
          </div>

          <div v-else-if="preProcessorDisplay.mode === 'groupSite'">
            <p class="text-body-2 mb-3">
              This account pulls vehicles from
              <strong>{{ preProcessorDisplay.sources.length }}</strong>
              source
              {{ preProcessorDisplay.sources.length === 1 ? 'dealer' : 'dealers' }}
              and merges them into its own inventory.
            </p>
            <div v-if="!preProcessorDisplay.sources.length" class="text-body-2 text-grey">
              Group Site mode is enabled but no source dealers are configured.
            </div>
            <v-expansion-panels v-else variant="accordion">
              <v-expansion-panel
                v-for="source in preProcessorDisplay.sources"
                :key="source.sourceJuid"
                :title="source.sourceJuid"
              >
                <template #text>
                  <div class="d-flex flex-wrap align-center ga-2 mb-3">
                    <v-chip v-if="source.sourceFeed" size="small" variant="tonal" color="primary">
                      feed: {{ source.sourceFeed }}
                    </v-chip>
                    <v-chip v-if="source.useSourceLocation" size="small" variant="tonal">
                      uses source dealer's location
                    </v-chip>
                  </div>
                  <div v-if="source.filterLines.length" class="text-body-2 mb-2">
                    Pulls vehicles where:
                  </div>
                  <ul v-if="source.filterLines.length" class="rule-lines mb-0">
                    <li v-for="(line, lineIndex) in source.filterLines" :key="lineIndex">
                      {{ line }}
                    </li>
                  </ul>
                  <div v-else class="text-body-2 text-grey">
                    Pulls every vehicle from this source dealer.
                  </div>
                </template>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <div v-else-if="preProcessorDisplay.mode === 'sharedInventory'">
            <p class="text-body-2 mb-3">
              This account receives a slice of a shared feed file based on filter rules.
              Each filter block below describes one slice this account claims.
            </p>
            <div class="d-flex flex-wrap align-center ga-2 mb-4">
              <v-chip v-if="preProcessorDisplay.feedSource" size="small" variant="tonal" color="primary">
                feed: {{ preProcessorDisplay.feedSource }}
              </v-chip>
              <v-chip v-if="preProcessorDisplay.pattern" size="small" variant="tonal">
                pattern: {{ preProcessorDisplay.pattern }}
              </v-chip>
            </div>
            <div v-if="!preProcessorDisplay.sharedFilters.length" class="text-body-2 text-grey">
              Shared Inventory mode is enabled but no filter blocks are defined,
              so no vehicles will be claimed.
            </div>
            <div v-else class="d-flex flex-column ga-3">
              <v-sheet
                v-for="(block, index) in preProcessorDisplay.sharedFilters"
                :key="`shared-${index}`"
                border
                rounded
                class="pa-4"
              >
                <div class="text-subtitle-2 mb-2">
                  Filter block {{ index + 1 }}
                </div>
                <div v-if="block.filterLines.length">
                  <ul class="rule-lines mb-0">
                    <li v-for="(line, lineIndex) in block.filterLines" :key="lineIndex">
                      {{ line }}
                    </li>
                  </ul>
                </div>
                <div v-else class="text-body-2 text-grey">
                  Empty filter — would claim every vehicle in this feed.
                </div>
              </v-sheet>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-if="feedConditionConfigs.length" class="border mb-4" elevation="0">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-tune-variant" class="mr-2" />
          Feed-Field Rules
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-expansion-panels variant="accordion">
            <v-expansion-panel
              v-for="config in feedConditionConfigs"
              :key="config.feed"
              :title="config.feed"
            >
              <template #text>
                <div class="d-flex flex-wrap align-center ga-2 mb-4">
                  <v-chip size="small" variant="tonal" color="primary">
                    {{ config.rules.length }} rules
                  </v-chip>
                  <v-chip v-if="config.defaultCondition" size="small" variant="tonal">
                    default: {{ config.defaultCondition }}
                  </v-chip>
                </div>
                <div class="d-flex flex-column ga-3">
                  <v-sheet
                    v-for="(rule, index) in config.rules"
                    :key="`${config.feed}-${index}`"
                    border
                    rounded
                    class="pa-4"
                  >
                    <div class="d-flex flex-wrap align-center ga-2 mb-2">
                      <v-chip v-if="rule.condition" size="small" variant="tonal" color="primary">
                        {{ rule.condition }}
                      </v-chip>
                      <v-chip size="small" variant="tonal">{{ rule.logic }}</v-chip>
                    </div>
                    <ul class="rule-lines mb-0">
                      <li v-for="(line, lineIndex) in rule.matches" :key="lineIndex">
                        {{ line }}
                      </li>
                    </ul>
                  </v-sheet>
                </div>
              </template>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <v-card class="border mb-4" elevation="0">
        <v-expansion-panels variant="accordion">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-icon icon="mdi-code-json" class="mr-2" />
                Raw Snapshot JSON
                <v-chip class="ms-2" size="x-small" variant="tonal">
                  preserved verbatim
                </v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="raw-json-wrap">
                <v-btn
                  class="raw-json-copy-btn"
                  size="small"
                  color="primary"
                  variant="tonal"
                  prepend-icon="mdi-content-copy"
                  @click="copySnapshotJson"
                >
                  {{ copyJsonLabel }}
                </v-btn>
                <pre class="raw-json">{{ snapshotJson }}</pre>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </div>
  </v-container>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useRuleConditionFieldsStore } from '@/stores/ruleConditionFields'
import {
  getLatestAccountConfigSnapshot,
  type AccountConfigSnapshot,
  type AccountConfigSnapshotResponse,
  type SnapshotMissingReason,
} from '@/services/accountConfigSnapshotService'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'

interface MappingRow {
  field: string
  sources: string[]
  sourceFieldPaths: Record<string, string>
  required: boolean
  onMissing: string
  defaultValue: string | null
  transform: string | null
}

interface MappingScope {
  key: string
  label: string
  mappings: MappingRow[]
  version: string | null
  lastUpdated: string | null
}

interface RuleGroupDisplay {
  action: string | null
  reason: string | null
  logic: string | null
  conditions: string[]
}

interface ListingRuleScopeDisplay {
  key: string
  label: string
  evaluationMode: string | null
  fallbackAction: string | null
  groups: RuleGroupDisplay[]
}

interface SegmentDisplay {
  key: string
  label: string
  priority: number | null
  exclusive: boolean
  custom: boolean
  logic: string | null
  conditions: string[]
}

interface FeedConditionRuleDisplay {
  condition: string | null
  logic: string
  matches: string[]
}

interface FeedConditionConfigDisplay {
  feed: string
  defaultCondition: string | null
  rules: FeedConditionRuleDisplay[]
}

type PreProcessorDisplayMode = 'groupSite' | 'sharedInventory' | 'none' | 'unknown'

interface PreProcessorSourceDisplay {
  sourceJuid: string
  sourceFeed: string | null
  useSourceLocation: boolean
  filterLines: string[]
}

interface PreProcessorFilterBlockDisplay {
  filterLines: string[]
}

interface PreProcessorDisplay {
  mode: PreProcessorDisplayMode
  productFound: boolean
  isActive: boolean
  sources: PreProcessorSourceDisplay[]
  feedSource: string | null
  pattern: string | null
  sharedFilters: PreProcessorFilterBlockDisplay[]
}

const endpoint = import.meta.env.VITE_ISE_BACKEND_URL as string
const accountStore = useAccountSettingsStore()
const ruleFieldsStore = useRuleConditionFieldsStore()

const isLoading = ref(false)
const snapshot = ref<AccountConfigSnapshot | null>(null)
const missingReason = ref<SnapshotMissingReason | null>(null)
const loadError = ref<string>('')
const mappingSearch = ref('')
const copyJsonLabel = ref('Copy JSON')
let copyJsonLabelTimeout: ReturnType<typeof setTimeout> | null = null

const normalized = computed(() => snapshot.value!.normalized)
const snapshotJson = computed(() => JSON.stringify(snapshot.value ?? {}, null, 2))
const downloadableReport = computed(() => ({
  jazelId: snapshot.value?.jazelId ?? '',
  generatedAt: snapshot.value?.generatedAt ?? null,
  primarySources: {
    effective: normalized.value.primarySources.effective,
    feeds: normalized.value.primarySources.feeds.map((feed) => ({
      feed: feed.feed,
      pattern: feed.pattern,
    })),
    ...(normalized.value.primarySources.unsupported.length
      ? { unsupported: normalized.value.primarySources.unsupported }
      : {}),
  },
  fieldMappings: mappingScopes.value.map((scope) => ({
    segment: scope.label,
    mappings: scope.mappings.map((mapping) => ({
      field: mapping.field,
      mappedFrom: Object.entries(mapping.sourceFieldPaths).map(
        ([source, path]) => `${source}: ${path}`,
      ),
      ...(mapping.defaultValue ? { defaultValue: mapping.defaultValue } : {}),
    })),
  })),
  listingRules: listingRuleScopes.value.map((scope) => ({
    segment: scope.label,
    rules: scope.groups.map((group) => ({
      action: group.action ? formatActionLabel(group.action) : null,
      appliesWhen: group.logic ? formatLogicLabel(group.logic) : null,
      conditions: group.conditions,
    })),
  })),
  segments: segments.value.map((segment) => ({
    segment: segment.label,
    description: describeSegment(segment),
    conditions: segment.conditions,
  })),
  feedConditionRules: feedConditionConfigs.value.map((config) => ({
    feed: config.feed,
    defaultCondition: config.defaultCondition,
    rules: config.rules,
  })),
  ...(preProcessorDisplay.value
    ? { preProcessor: buildPreProcessorReport(preProcessorDisplay.value) }
    : {}),
}))

const filteredMappingScopes = computed<MappingScope[]>(() => {
  const query = mappingSearch.value.trim().toLowerCase()
  if (!query) return mappingScopes.value
  return mappingScopes.value
    .map((scope) => ({
      ...scope,
      mappings: scope.mappings.filter((mapping) => {
        const haystack = [
          mapping.field,
          mapping.transform || '',
          mapping.onMissing,
          mapping.defaultValue || '',
          ...mapping.sources,
          ...Object.entries(mapping.sourceFieldPaths).flatMap(([source, path]) => [source, path]),
        ]
          .join(' ')
          .toLowerCase()
        return haystack.includes(query)
      }),
    }))
    .filter((scope) => scope.mappings.length > 0)
})

const mappingScopes = computed<MappingScope[]>(() => {
  const raw = snapshot.value?.raw?.fieldMapping as any
  const mappingConfig = raw?.mapping_config
  if (!mappingConfig || typeof mappingConfig !== 'object') return []

  return Object.entries(mappingConfig).flatMap(([scopeKey, scopeValue]) => {
    const mappings = Array.isArray((scopeValue as any)?.field_mapping?.mappings)
      ? (scopeValue as any).field_mapping.mappings
      : []
    if (!mappings.length) return []

    return [
      {
        key: scopeKey,
        label: formatScopeLabel(scopeKey),
        version: stringOrNull(raw?.version),
        lastUpdated: stringOrNull(raw?.lastUpdated),
        mappings: mappings.map((mapping: any) => ({
          field: String(mapping?.field || 'unknown'),
          sources: Array.isArray(mapping?.sources) ? mapping.sources.map(String) : [],
          sourceFieldPaths:
            mapping?.source_field_paths && typeof mapping.source_field_paths === 'object'
              ? Object.fromEntries(
                  Object.entries(mapping.source_field_paths).map(([source, path]) => [
                    source,
                    String(path ?? ''),
                  ]),
                )
              : {},
          required: Boolean(mapping?.required),
          onMissing: String(mapping?.policy?.on_missing || 'fallback'),
          defaultValue:
            mapping?.default_value != null
              ? String(mapping.default_value)
              : mapping?.policy?.default_value != null
                ? String(mapping.policy.default_value)
                : null,
          transform: describeMappingTransform(mapping),
        })),
      },
    ]
  })
})

const listingRuleScopes = computed<ListingRuleScopeDisplay[]>(() => {
  const raw = snapshot.value?.raw?.listingRules as any
  if (!raw || typeof raw !== 'object') return []

  return Object.entries(raw).flatMap(([scopeKey, scopeValue]) => {
    const groups = Array.isArray((scopeValue as any)?.inventory_membership_rules?.ruleset?.rules)
      ? (scopeValue as any).inventory_membership_rules.ruleset.rules
      : []
    if (!groups.length) return []

    return [
      {
        key: scopeKey,
        label: formatScopeLabel(scopeKey),
        evaluationMode: stringOrNull((scopeValue as any)?.inventory_membership_rules?.evaluation?.mode),
        fallbackAction: stringOrNull((scopeValue as any)?.inventory_membership_rules?.evaluation?.fallback_action),
        groups: groups.map((group: any) => {
          const wrappedGroup =
            group?.type === 'rule'
              ? {
                  action: group?.action || 'block',
                  reason: group?.reason || null,
                  logic: 'AND',
                  rules: [group],
                }
              : group
          return {
            action: stringOrNull(wrappedGroup?.action),
            reason: stringOrNull(wrappedGroup?.reason),
            logic: stringOrNull(wrappedGroup?.logic),
            conditions: describeConditions(wrappedGroup?.rules),
          }
        }),
      },
    ]
  })
})

const segments = computed<SegmentDisplay[]>(() => {
  const segmentMap = (snapshot.value?.raw?.segmentation as any)?.segments
  if (!segmentMap || typeof segmentMap !== 'object') return []

  return Object.entries(segmentMap)
    .map(([segmentKey, segmentValue]) => ({
      key: segmentKey,
      label: stringOrNull((segmentValue as any)?.name) || formatScopeLabel(segmentKey),
      priority: numberOrNull((segmentValue as any)?.priority),
      exclusive: Boolean((segmentValue as any)?.exclusive),
      custom: Boolean((segmentValue as any)?.custom),
      logic: stringOrNull((segmentValue as any)?.ruleset?.logic),
      conditions: describeConditions((segmentValue as any)?.ruleset?.rules),
    }))
    .sort((a, b) => (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER))
})

const preProcessorDisplay = computed<PreProcessorDisplay | null>(() => {
  const snap = snapshot.value
  if (!snap) return null

  const normalizedPP = (snap.normalized as any)?.preProcessor
  const rawPP = unwrapJamsRaw((snap.raw as any)?.preProcessor)

  // Older snapshots predate pre-processor support entirely. Hide the section
  // rather than implying "none" for a config bucket we never read.
  if (!normalizedPP && rawPP == null) return null

  const productFound = Boolean(normalizedPP?.productFound) || rawPP != null
  const isActive = normalizedPP?.isActive ?? (rawPP?.Enable !== false)
  const mode: PreProcessorDisplayMode = (normalizedPP?.mode as PreProcessorDisplayMode) ?? inferPreProcessorMode(rawPP)

  const sources: PreProcessorSourceDisplay[] = []
  if (mode === 'groupSite' && Array.isArray(rawPP?.sources)) {
    for (const src of rawPP.sources) {
      if (!src || typeof src !== 'object') continue
      const sourceJuid = stringOrNull((src as any).sourceJuid)
      if (!sourceJuid) continue
      sources.push({
        sourceJuid,
        sourceFeed: stringOrNull((src as any).sourceFeed),
        useSourceLocation: (src as any).useSourceLocation === true,
        filterLines: describePreProcessorFilter((src as any).filter),
      })
    }
  }

  const sharedFilters: PreProcessorFilterBlockDisplay[] = []
  let feedSource: string | null = null
  let pattern: string | null = null
  if (mode === 'sharedInventory' && rawPP) {
    feedSource = stringOrNull((rawPP as any).feedSource)
    pattern = stringOrNull((rawPP as any).pattern)
    const rawFilters = Array.isArray((rawPP as any).filters)
      ? (rawPP as any).filters
      : (rawPP as any).filter && typeof (rawPP as any).filter === 'object'
        ? [(rawPP as any).filter]
        : []
    for (const f of rawFilters) {
      sharedFilters.push({ filterLines: describePreProcessorFilter(f) })
    }
  }

  return {
    mode,
    productFound,
    isActive,
    sources,
    feedSource,
    pattern,
    sharedFilters,
  }
})

const feedConditionConfigs = computed<FeedConditionConfigDisplay[]>(() => {
  const raw = snapshot.value?.raw as any
  const feedConditionRoot = raw?.feedConditionRules ?? raw?.feed_condition_rules
  const feeds = Array.isArray(feedConditionRoot?.feeds)
    ? feedConditionRoot.feeds
    : Array.isArray(feedConditionRoot)
      ? feedConditionRoot
      : []

  return feeds.map((feedConfig: any) => ({
    feed: String(feedConfig?.feed || 'unknown'),
    defaultCondition: stringOrNull(feedConfig?.default_condition),
    rules: Array.isArray(feedConfig?.rules)
      ? feedConfig.rules.map((rule: any) => ({
          condition: stringOrNull(rule?.condition),
          logic: String(rule?.logic || 'AND'),
          matches: Array.isArray(rule?.match)
            ? rule.match.map(
                (match: any) =>
                  `${String(match?.field || 'field')} ${String(match?.operator || 'matches')} ${formatValue(match?.value)}`,
              )
            : [],
        }))
      : [],
  }))
})

const emptyState = computed(() => {
  if (loadError.value) {
    return {
      icon: 'mdi-alert-circle-outline',
      iconColor: 'error',
      title: 'Could not load configuration',
      message:
        'We were unable to load the configuration snapshot. Please retry, or contact support if this persists.',
    }
  }
  switch (missingReason.value) {
    case 'NO_SNAPSHOTS_YET':
      return {
        icon: 'mdi-clipboard-text-clock-outline',
        iconColor: 'grey',
        title: 'No published configuration yet',
        message:
          'A snapshot is captured the first time a publish actually changes configuration. Publish a change to populate this view.',
      }
    case 'POINTER_INCONSISTENT':
      return {
        icon: 'mdi-alert-octagon-outline',
        iconColor: 'warning',
        title: 'Latest snapshot is unavailable',
        message:
          'The pointer to the latest snapshot references a file that could not be read. This is rare and usually indicates an interrupted publish. The next configuration change will repair this view.',
      }
    case 'NO_CONFIG_CHANGE_IN_THIS_PUBLISH':
      return {
        icon: 'mdi-information-outline',
        iconColor: 'grey',
        title: 'No snapshot for this publish',
        message:
          'This publish completed but did not change any configuration, so no snapshot was written.',
      }
    case 'READ_ERROR':
      return {
        icon: 'mdi-cloud-alert-outline',
        iconColor: 'error',
        title: 'Snapshot service unavailable',
        message:
          'A transient error occurred while reading the snapshot. Please retry in a few moments.',
      }
    default:
      return {
        icon: 'mdi-clipboard-outline',
        iconColor: 'grey',
        title: 'No configuration to display',
        message: 'There is no published configuration available for this account.',
      }
  }
})

function applyResponse(res: AccountConfigSnapshotResponse) {
  if (res.snapshot) {
    snapshot.value = res.snapshot
    missingReason.value = null
  } else {
    snapshot.value = null
    missingReason.value = res.missingReason ?? null
  }
}

async function loadLatest() {
  if (!accountStore.accountId) {
    snapshot.value = null
    missingReason.value = null
    loadError.value = ''
    return
  }
  if (!endpoint) {
    loadError.value = 'Backend endpoint is not configured (VITE_ISE_BACKEND_URL).'
    snapshot.value = null
    return
  }
  loadError.value = ''
  isLoading.value = true
  try {
    const res = await getLatestAccountConfigSnapshot(endpoint, accountStore.accountId)
    applyResponse(res)
  } catch (e) {
    snapshot.value = null
    missingReason.value = null
    loadError.value = e instanceof Error ? e.message : String(e)
  } finally {
    isLoading.value = false
  }
}

function formatUnsupported(item: unknown): string {
  if (item == null) return 'null'
  if (typeof item === 'string') return item
  try {
    return JSON.stringify(item)
  } catch {
    return String(item)
  }
}

function stringOrNull(value: unknown): string | null {
  if (value == null) return null
  const text = String(value).trim()
  return text ? text : null
}

function numberOrNull(value: unknown): number | null {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function formatScopeLabel(key: string): string {
  if (key === 'all') return 'All Vehicles'
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => formatValue(item)).join(', ')
  if (value == null) return 'null'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

function describeMappingTransform(mapping: any): string | null {
  if (stringOrNull(mapping?.function)) return String(mapping.function)
  if (stringOrNull(mapping?.type)) return String(mapping.type)
  return null
}

function humanizeKey(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatFieldLabel(field: string): string {
  // Rule-condition field labels come from the shared catalog (single source of
  // truth) so naming stays consistent with the Publish Rules / Vehicle Grouping
  // editors. Non-catalog fields fall back to humanization.
  return ruleFieldsStore.labelFor(field) || humanizeKey(field).replace(/^Vehicle /, 'vehicle ')
}

function formatReasonLabel(reason: string): string {
  return humanizeKey(reason)
}

function formatActionLabel(action: string): string {
  const labels: Record<string, string> = {
    block: 'hide vehicle',
    allow: 'continue when no block rule matches',
  }
  return labels[action] || humanizeKey(action).toLowerCase()
}

function formatLogicLabel(logic: string): string {
  const labels: Record<string, string> = {
    AND: 'all must match',
    OR: 'any can match',
  }
  return labels[logic] || logic
}

function formatEvaluationMode(mode: string): string {
  const labels: Record<string, string> = {
    first_match: 'first matching block rule hides vehicle',
  }
  return labels[mode] || humanizeKey(mode).toLowerCase()
}

function describeSegment(segment: SegmentDisplay): string {
  if (segment.key === 'all') {
    return 'This group catches vehicles that do not match a more specific group.'
  }

  const matchText =
    segment.logic === 'OR'
      ? 'Vehicles are placed in this group when any of the following are true.'
      : 'Vehicles are placed in this group when all of the following are true.'

  if (segment.custom) {
    return `This is a custom group. ${matchText}`
  }

  return matchText
}

function formatOperatorPhrase(operator: string): string {
  const phrases: Record<string, string> = {
    equals: 'is',
    contains: 'contains',
    not_contains: 'does not contain',
    in: 'is one of',
    not_in: 'is not one of',
    length_not_in: 'length is not',
    length_in: 'length is',
    startsWith: 'starts with',
    greaterThan: 'is greater than',
    lessThanOrEqual: 'is at most',
    regex: 'matches pattern',
  }
  return phrases[operator] || humanizeKey(operator).toLowerCase()
}

function describeRule(rule: any): string[] {
  if (!rule || typeof rule !== 'object') return []
  if (Array.isArray(rule?.rules)) {
    return rule.rules.flatMap((child: any) => describeRule(child))
  }
  if (!stringOrNull(rule?.field) && !stringOrNull(rule?.operator)) return []
  const field = String(rule.field || 'field')
  const operator = String(rule.operator || 'matches')
  const formattedValue = formatValue(rule.value)

  if (operator === 'length_not_in' || operator === 'length_in') {
    return [`${formatFieldLabel(field)} length ${operator === 'length_not_in' ? 'is not' : 'is'} ${formattedValue}`]
  }

  return [`${formatFieldLabel(field)} ${formatOperatorPhrase(operator)} ${formattedValue}`]
}

function describeConditions(rules: unknown): string[] {
  return Array.isArray(rules) ? rules.flatMap((rule) => describeRule(rule)) : []
}

function unwrapJamsRaw(payload: unknown): any {
  if (!payload || typeof payload !== 'object') return payload ?? null
  const p = payload as Record<string, unknown>
  if (p.config && typeof p.config === 'object') return p.config
  if (p.Config && typeof p.Config === 'object') return p.Config
  return p
}

function inferPreProcessorMode(raw: any): PreProcessorDisplayMode {
  if (!raw || typeof raw !== 'object') return 'none'
  if (raw.sharedInventory === true) return 'sharedInventory'
  if (raw.groupSite === true || Array.isArray(raw.sources)) return 'groupSite'
  return 'unknown'
}

function formatPreProcessorMode(mode: PreProcessorDisplayMode): string {
  switch (mode) {
    case 'groupSite':
      return 'Group Site'
    case 'sharedInventory':
      return 'Shared Inventory'
    case 'none':
      return 'Not configured'
    case 'unknown':
    default:
      return 'Unrecognized'
  }
}

function preProcessorModeColor(mode: PreProcessorDisplayMode): string {
  switch (mode) {
    case 'groupSite':
    case 'sharedInventory':
      return 'primary'
    case 'unknown':
      return 'warning'
    case 'none':
    default:
      return 'grey'
  }
}

function formatNumberWithCommas(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('en-US') : String(value)
}

function buildPreProcessorReport(display: PreProcessorDisplay) {
  const base = {
    mode: formatPreProcessorMode(display.mode),
    active: display.isActive,
  }
  if (display.mode === 'groupSite') {
    return {
      ...base,
      sources: display.sources.map((source) => ({
        sourceJuid: source.sourceJuid,
        ...(source.sourceFeed ? { feed: source.sourceFeed } : {}),
        ...(source.useSourceLocation ? { usesSourceLocation: true } : {}),
        pulls: source.filterLines.length ? source.filterLines : ['every vehicle from this source'],
      })),
    }
  }
  if (display.mode === 'sharedInventory') {
    return {
      ...base,
      ...(display.feedSource ? { feed: display.feedSource } : {}),
      ...(display.pattern ? { pattern: display.pattern } : {}),
      filterBlocks: display.sharedFilters.map((block) =>
        block.filterLines.length ? block.filterLines : ['every vehicle in this feed'],
      ),
    }
  }
  return base
}

function describePreProcessorFilter(filter: unknown): string[] {
  if (!filter || typeof filter !== 'object') return []
  const f = filter as Record<string, unknown>
  const lines: string[] = []

  if (Array.isArray(f.condition) && f.condition.length > 0) {
    const conds = f.condition.filter((c): c is string => typeof c === 'string')
    if (conds.length === 1) {
      lines.push(`condition is ${conds[0]}`)
    } else if (conds.length > 1) {
      lines.push(`condition is one of ${conds.join(', ')}`)
    }
  } else if (typeof f.condition === 'string' && f.condition.trim()) {
    lines.push(`condition is ${f.condition.trim()}`)
  }

  const stringFields: Array<[string, string]> = [
    ['make', 'make'],
    ['model', 'model'],
    ['trim', 'trim'],
    ['bodyType', 'body type'],
    ['drivetrain', 'drivetrain'],
    ['fuelType', 'fuel type'],
    ['exteriorColor', 'exterior color'],
    ['interiorColor', 'interior color'],
  ]
  for (const [key, label] of stringFields) {
    const v = f[key]
    if (typeof v === 'string' && v.trim()) lines.push(`${label} is ${v.trim()}`)
  }

  const yearMin = typeof f.yearMin === 'number' ? f.yearMin : null
  const yearMax = typeof f.yearMax === 'number' ? f.yearMax : null
  if (yearMin != null && yearMax != null) {
    lines.push(`year between ${yearMin} and ${yearMax}`)
  } else if (yearMin != null) {
    lines.push(`year is at least ${yearMin}`)
  } else if (yearMax != null) {
    lines.push(`year is at most ${yearMax}`)
  }

  const priceMin = typeof f.priceMin === 'number' ? f.priceMin : null
  const priceMax = typeof f.priceMax === 'number' ? f.priceMax : null
  if (priceMin != null && priceMax != null) {
    lines.push(`price between $${formatNumberWithCommas(priceMin)} and $${formatNumberWithCommas(priceMax)}`)
  } else if (priceMin != null) {
    lines.push(`price is at least $${formatNumberWithCommas(priceMin)}`)
  } else if (priceMax != null) {
    lines.push(`price is at most $${formatNumberWithCommas(priceMax)}`)
  }

  const mileageMin = typeof f.mileageMin === 'number' ? f.mileageMin : null
  const mileageMax = typeof f.mileageMax === 'number' ? f.mileageMax : null
  if (mileageMin != null && mileageMax != null) {
    lines.push(`mileage between ${formatNumberWithCommas(mileageMin)} and ${formatNumberWithCommas(mileageMax)} mi`)
  } else if (mileageMin != null) {
    lines.push(`mileage is at least ${formatNumberWithCommas(mileageMin)} mi`)
  } else if (mileageMax != null) {
    lines.push(`mileage is at most ${formatNumberWithCommas(mileageMax)} mi`)
  }

  return lines
}

function downloadSnapshot() {
  if (!snapshot.value) return
  const payload = JSON.stringify(downloadableReport.value, null, 2)
  const blob = new Blob([payload], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${snapshot.value.jazelId}_inv_config.json`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

async function copySnapshotJson() {
  const text = snapshotJson.value
  try {
    await navigator.clipboard.writeText(text)
    setCopyJsonLabel('Copied')
  } catch {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopyJsonLabel('Copied')
    } catch {
      setCopyJsonLabel('Copy failed')
    }
  }
}

function setCopyJsonLabel(label: string) {
  copyJsonLabel.value = label
  if (copyJsonLabelTimeout) clearTimeout(copyJsonLabelTimeout)
  copyJsonLabelTimeout = setTimeout(() => {
    copyJsonLabel.value = 'Copy JSON'
    copyJsonLabelTimeout = null
  }, 2000)
}

onMounted(() => {
  ruleFieldsStore.load()
  loadLatest()
})
watch(() => accountStore.accountId, loadLatest)
</script>

<style scoped>
.text-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.875rem;
}

.mapping-search {
  width: min(100%, 360px);
}

.rule-lines {
  margin: 0;
  padding-left: 1.25rem;
}

.rule-lines li + li {
  margin-top: 0.375rem;
}

.raw-json-wrap {
  position: relative;
}

.raw-json-copy-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
}

.raw-json {
  white-space: pre-wrap;
  overflow: auto;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
  box-sizing: border-box;
  max-height: 480px;
  background-color: #f8fafc;
  color: #334155;
  border: 1px solid #e2e8f0;
  padding: 56px 12px 12px;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8125rem;
}
</style>
